import React, { useState } from "react";
import emailjs from "emailjs-com";
import { openNirkanaWhatsApp } from "../lib/whatsapp";

const initialState = {
  name: "",
  phone: "",
  email: "",
  eventDate: "",
  guestCount: "",
  locality: "",
  message: "",
};

const buildWhatsAppMessage = (data) =>
  `New quote request from the website:\n` +
  `Name: ${data.name}\n` +
  `Phone: ${data.phone}\n` +
  (data.email ? `Email: ${data.email}\n` : "") +
  `Event date: ${data.eventDate}\n` +
  `Guests: ${data.guestCount}\n` +
  `Area: ${data.locality}` +
  (data.message ? `\nNotes: ${data.message}` : "");

const Book = () => {
  const [formData, setFormData] = useState(initialState);
  const [status, setStatus] = useState(null); // "sending" | "success" | "error"

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");

    // Open the pre-filled WhatsApp chat first and synchronously — this is
    // the reliable channel here, and must fire before any `await` or
    // browsers will block it as a popup.
    openNirkanaWhatsApp(buildWhatsAppMessage(formData));

    const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const templateId = process.env.REACT_APP_EMAILJS_QUOTE_TEMPLATE_ID;
    const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

    if (serviceId && templateId && publicKey) {
      try {
        await emailjs.send(serviceId, templateId, formData, publicKey);
      } catch (err) {
        // Email to info@nirkana.com failed (or isn't configured yet) — the
        // WhatsApp message already went out above, so don't block or
        // alarm the customer over it. Log for whoever's testing locally.
        console.warn("Quote request email failed to send:", err);
      }
    } else {
      console.warn("EmailJS env vars not set — quote request only sent via WhatsApp.");
    }

    setStatus("success");
    setFormData(initialState);
  };

  return (
    <section className="bg-cloud py-16 lg:py-24">
      <div className="max-w-content mx-auto grid grid-cols-1 gap-12 px-6 lg:grid-cols-2 lg:gap-16 lg:px-10">
        <div>
          <h2 className="text-3xl font-bold text-brand sm:text-4xl">
            Get your free quote
          </h2>
          <p className="mt-4 max-w-prose text-muted">
            Tell us the basics and we'll come back with a per-guest quote
            and a short list of what's included. No payment or commitment
            at this stage.
          </p>

          <div className="mt-10 space-y-4 text-sm text-muted">
            <p>Usually respond within one business day.</p>
            <p>Serving Chennai and surrounding areas for Phase 1.</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="rounded-2xl bg-paper p-6 sm:p-8">
          {status === "success" && (
            <div className="mb-5 rounded-lg bg-success/10 px-4 py-3 text-sm text-charcoal">
              Thanks — WhatsApp should have opened with your details ready
              to send. Tap send there and we'll be in touch shortly.
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <input
              type="text" name="name" required placeholder="Full name *"
              value={formData.name} onChange={handleChange}
              className="rounded-lg border border-line px-4 py-3 text-charcoal placeholder:text-muted focus:border-action sm:col-span-2"
            />
            <input
              type="tel" name="phone" required placeholder="Phone number *"
              value={formData.phone} onChange={handleChange}
              className="rounded-lg border border-line px-4 py-3 text-charcoal placeholder:text-muted focus:border-action"
            />
            <input
              type="email" name="email" placeholder="Email (optional)"
              value={formData.email} onChange={handleChange}
              className="rounded-lg border border-line px-4 py-3 text-charcoal placeholder:text-muted focus:border-action"
            />
            <input
              type="date" name="eventDate" required
              value={formData.eventDate} onChange={handleChange}
              className="rounded-lg border border-line px-4 py-3 text-charcoal focus:border-action"
            />
            <input
              type="number" min="1" name="guestCount" required placeholder="Estimated guests *"
              value={formData.guestCount} onChange={handleChange}
              className="rounded-lg border border-line px-4 py-3 text-charcoal placeholder:text-muted focus:border-action"
            />
            <input
              type="text" name="locality" placeholder="Area / locality" required
              value={formData.locality} onChange={handleChange}
              className="rounded-lg border border-line px-4 py-3 text-charcoal placeholder:text-muted focus:border-action sm:col-span-2"
            />
            <textarea
              name="message" rows="3" placeholder="Anything else we should know? (optional)"
              value={formData.message} onChange={handleChange}
              className="rounded-lg border border-line px-4 py-3 text-charcoal placeholder:text-muted focus:border-action sm:col-span-2"
            />
          </div>

          <button
            type="submit"
            disabled={status === "sending"}
            className="mt-6 w-full rounded-full bg-action px-6 py-3.5 text-base font-medium text-paper hover:bg-action/90 transition-colors disabled:opacity-60"
          >
            {status === "sending" ? "Sending..." : "Request my quote"}
          </button>
        </form>
      </div>
    </section>
  );
};

export default Book;
